import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import resources from "../../resources/Content";

const revealContainer = {
  start: {},
  end: {
    transition: {
      staggerChildren: 0.15,
      when: "beforeChildren",
    },
  },
};

const splitTextLeft = {
  start: { opacity: 1, x: "22vw" },
  end: {
    opacity: 1,
    x: "-1vw",
    transition: { duration: 1.2, ease: [0.33, 1, 0.68, 1] },
  },
};

const splitTextRight = {
  start: { opacity: 1, x: "-19vw" },
  end: {
    opacity: 1,
    x: "1vw",
    transition: {
      duration: 1.2,
      ease: [0.33, 1, 0.68, 1],
    },
  },
};

const imageReveal = {
  start: { opacity: 0, scale: 0.2, rotateY: 90 },
  end: {
    opacity: 1,
    scale: 1,
    rotateY: 0,
    transition: { duration: 1.3, ease: [0.16, 1, 0.3, 1] },
  },
};

/* ================= COMPONENT ================= */

export default function AboutHero() {
  const videoRef = useRef(null);

  /* ================= CMS (SAFE ACCESS) ================= */

  const hero = resources?.about?.hero || {};

  const {
    video,
    image,
    left = {},
    right = {},
  } = hero;

  const {
    subtitle = "",
    title: leftTitle = "",
  } = left;

  const {
    title: rightTitle = "",
    description = "",
  } = right;

  /* ================= VIDEO HANDLING (PROD SAFE) ================= */

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    const attemptPlay = async () => {
      try {
        await videoEl.play();
      } catch {
        videoEl.muted = true;
        try {
          await videoEl.play();
        } catch {
          // silent fail (expected on some browsers)
        }
      }
    };

    attemptPlay();
  }, []);

  return (
    <section className="relative min-h-screen w-full bg-black text-white overflow-hidden">
      {/* Background Video */}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover z-0"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
      >
        {video && <source src={video} type="video/mp4" />}
      </video>

      <div className="absolute inset-0 bg-black/40 z-10" />

      <motion.div
        variants={revealContainer}
        initial="start"
        animate="end"
        className="
          relative z-20 mx-auto max-w-[1500px]
          px-[3rem]
          pb-24
          pt-36 sm:pt-36 md:pt-40 lg:pt-56
        "
      >
        {/* ================= MOBILE & TABLET ================= */}

        <div className="flex flex-col items-center gap-12 text-center lg:hidden w-full">
          {/* LEFT TEXT */}
          <motion.div variants={splitTextLeft} className="w-full px-4">
            <p className="text-sm tracking-wide text-neutral-300 mb-2">
              {subtitle}
            </p>

            <h1
              className="
                font-antonio
                font-extrabold md:font-[700] uppercase tracking-[-0.03em]
                text-[clamp(2.5rem,12.5vw,5rem)]
                leading-[0.95] break-words
              "
            >
              {leftTitle}
            </h1>
          </motion.div>

          {/* IMAGE */}
          <motion.div
            variants={imageReveal}
            initial="start"
            animate="end"
            className="
              w-[70vw] max-w-[280px]
              aspect-[3/4] z-30
              rounded-2xl overflow-hidden shadow-2xl
              backdrop-blur-md bg-white/10 border border-gray-300
            "
          >
            {image && (
              <img
                src={image}
                alt={leftTitle || "Hero"}
                className="w-full h-full object-cover object-top"
                loading="eager"
              />
            )}
          </motion.div>

          {/* RIGHT TEXT */}
          <motion.div variants={splitTextRight} className="w-full px-4">
            <h1
              className="
                font-antonio font-extrabold md:font-[700] uppercase tracking-[-0.03em]
                text-[clamp(2.5rem,14vw,5rem)]
                leading-[0.95]text-[#9bb7d4] z-20
              "
            >
              {rightTitle}
            </h1>

            <p className="mt-4 text-[15px] sm:text-base leading-relaxed text-neutral-100 max-w-md mx-auto">
              {description}
            </p>
          </motion.div>
        </div>

        {/* ================= DESKTOP ================= */}

        <div className="relative hidden lg:flex flex-row justify-center">
          {/* LEFT TEXT */}
          <motion.h1
            variants={splitTextLeft}
            className="
              relative z-10
              font-antonio font-[700] uppercase tracking-[-0.03em]
              text-[24vw] sm:text-[18vw] md:text-[10vw] lg:text-[7vw]
              leading-[0.9] text-[#fff] pb-[3rem] content-evenly
            "
          >
            <p className="text-lg font-medium tracking-wide text-neutral-100">
              {subtitle}
            </p>

            <div className="h-[1px] w-10 bg-neutral-900/60" />

            {leftTitle}
          </motion.h1>

          {/* CENTER IMAGE */}
          <motion.div
            variants={imageReveal}
            className="
              relative z-30 
              backdrop-blur-md bg-white/10
              w-[clamp(260px,18vw,380px)]
              flex-shrink-0
              border border-gray-300
              aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl
            "
          >
            {image && (
              <img
                src={image}
                alt={leftTitle || "Hero"}
                className="w-full h-full object-cover object-top"
                loading="eager"
              />
            )}
          </motion.div>

          {/* RIGHT TEXT */}
          <motion.h1
            variants={splitTextRight}
            className="
              relative z-10
              font-antonio font-[700] 
              text-[24vw] sm:text-[18vw] md:text-[10vw] lg:text-[7vw]
              leading-[0.9] text-left text-[#9bb7d4] pt-[5rem] content-evenly
            "
          >
            <span className="uppercase">{rightTitle}</span>

            <p className="font-light pt-8 text-lg leading-relaxed text-neutral-100 max-w-96">
              {description}
            </p>
          </motion.h1>
        </div>
      </motion.div>
    </section>
  );
}